import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  Clock,
  LifeBuoy,
  Loader2,
  Mail,
  Phone,
  User,
  Wrench,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionCard } from '@/components/layout/SectionCard';
import { useCustomer } from '@/hooks/useCustomer';
import { useSession } from '@/lib/auth';
import { useEquipmentAssets, type EquipmentAssetRow } from '@/hooks/useEquipmentRentals';
import {
  kindLabel,
  useCreateServiceRequest,
  useServiceRequests,
  useUpdateServiceRequestStatus,
  type ServiceRequest,
  type ServiceRequestKind,
  type ServiceRequestStatus,
} from '@/hooks/useServiceRequests';
import {
  useRecentServiceJobs,
  type RecentServiceJob,
} from '@/hooks/useRecentServiceJobs';
import { cn } from '@/lib/utils';

import { Loader2 as ChainSwitchSpinner } from 'lucide-react';
import { useGroupAssets } from '@/hooks/useGroupAssets';
import ChainService from '@/pages/chain/ChainService';

const KIND_OPTIONS: ReadonlyArray<{ value: ServiceRequestKind; label: string }> = [
  { value: 'equipment_issue', label: 'Equipment issue' },
  { value: 'refill', label: 'Refill' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'other', label: 'Other' },
];

/**
 * /service — standalone service requests page. Mirror of the inline card
 * on /home but with proper equipment selection + structured contact info.
 *
 * Layout:
 *   1. Composer card (navy header) — kind, equipment dropdown, subject,
 *      description, name/phone/email contact, submit.
 *   2. Open tickets list — same row treatment as the home dashboard.
 *   3. Recent Brix service visits (last 90 days from SF) when available.
 */
function SingleServiceRequests() {
  const { data: ctx } = useCustomer();
  const { data: tickets = [], isLoading: ticketsLoading } = useServiceRequests({ onlyOpen: true });
  const { data: assets = [] } = useEquipmentAssets();
  // Last 30 days of Brix service work — open + completed — pulled from
  // Service Fusion. Skips jobs that already landed on a QBO invoice
  // (those are deliveries, not service visits).
  const { data: openJobs = [], isLoading: openJobsLoading } = useRecentServiceJobs(30, 'open');
  const { data: completedJobs = [], isLoading: completedJobsLoading } =
    useRecentServiceJobs(30, 'completed');

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-20 md:pb-10">
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-6 pb-12 space-y-6">
        <SectionCard
          icon={<LifeBuoy size={20} />}
          title="Service requests"
          subtitle="Tell us what's going on. Pick the equipment, leave a callback number, and our dispatch team takes it from there."
        >
          {ctx ? (
            <Composer
              customerId={ctx.customer.id}
              defaultLocationId={ctx.locations[0]?.id ?? null}
              assets={assets}
            />
          ) : (
            <div className="text-sm text-slate-900 tracking-tight inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          )}
        </SectionCard>

        <SectionCard
          icon={<Clock size={20} />}
          title="Open tickets"
          subtitle="Open and in-progress tickets land here. Resolved ones drop off so the list stays useful."
        >
          {ticketsLoading ? (
            <div className="text-sm text-slate-900 tracking-tight inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading tickets…
            </div>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-slate-900 tracking-tight leading-relaxed">
              No open tickets right now. Submit one above and it'll appear
              here + email the dispatch team at the same time.
            </p>
          ) : (
            <ul className="divide-y divide-brix-border -my-2">
              {tickets.map((t) => (
                <TicketRow key={t.id} ticket={t} />
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          icon={<Wrench size={20} />}
          title="Service history · last 30 days"
          subtitle="Live feed of service work for this account — what's still open with the dispatch team, plus what's already been completed."
        >
          {openJobsLoading || completedJobsLoading ? (
            <div className="text-sm text-slate-900 tracking-tight inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading service history…
            </div>
          ) : openJobs.length === 0 && completedJobs.length === 0 ? (
            <p className="text-sm text-slate-900 tracking-tight leading-relaxed">
              No service activity in the last 30 days. New work
              you submit above will appear here once dispatch books it.
            </p>
          ) : (
            <div className="space-y-5">
              <div>
                <div className="text-xs tracking-tight font-semibold text-slate-900 mb-2 inline-flex items-center gap-1.5">
                  <Wrench size={12} /> Open / in progress ({openJobs.length})
                </div>
                {openJobs.length === 0 ? (
                  <p className="text-xs text-slate-900/70 italic tracking-tight">
                    Nothing in the queue right now.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {openJobs.map((j) => (
                      <SfJobRow key={j.jobId} job={j} tone="open" />
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-xs tracking-tight font-semibold text-slate-900 mb-2 inline-flex items-center gap-1.5">
                  <CheckCircle2 size={12} /> Completed ({completedJobs.length})
                </div>
                {completedJobs.length === 0 ? (
                  <p className="text-xs text-slate-900/70 italic tracking-tight">
                    No completed work in the last 30 days.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {completedJobs.slice(0, 12).map((j) => (
                      <SfJobRow key={j.jobId} job={j} tone="completed" />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

/* ---------------------------- composer ---------------------------- */

interface ComposerProps {
  customerId: string;
  defaultLocationId: string | null;
  assets: EquipmentAssetRow[];
}

function Composer({ customerId, defaultLocationId, assets }: ComposerProps) {
  const { session } = useSession();
  const { data: ctx } = useCustomer();
  const { mutate, isPending, error } = useCreateServiceRequest();

  const [kind, setKind] = useState<ServiceRequestKind>('equipment_issue');
  const [assetId, setAssetId] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [contactName, setContactName] = useState(ctx?.user.fullName ?? '');
  const [contactPhone, setContactPhone] = useState(ctx?.user.phone ?? '');
  const [contactEmail, setContactEmail] = useState(session?.user?.email ?? '');
  const [showOk, setShowOk] = useState(false);

  const selectedAsset = useMemo(
    () => assets.find((a) => a.id === assetId) ?? null,
    [assets, assetId],
  );

  // When you pick an asset, auto-fill the subject so dispatch sees it
  // at a glance. Customer can still edit before submitting.
  function handleAssetChange(id: string) {
    setAssetId(id);
    if (!id) return;
    const a = assets.find((x) => x.id === id);
    if (!a) return;
    const title = a.catalogName ?? a.qboItemName ?? a.description ?? 'Equipment unit';
    setSubject(
      `Service request — ${title}${a.serialNumber ? ` (SN ${a.serialNumber})` : ''}`,
    );
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    if (!contactName.trim() || !contactPhone.trim()) return;

    const lines: string[] = [];
    if (selectedAsset) {
      const a = selectedAsset;
      const title = a.catalogName ?? a.qboItemName ?? a.description ?? 'Equipment unit';
      lines.push(`Equipment: ${title}`);
      const makeModel = [a.make, a.modelNumber].filter(Boolean).join(' / ');
      if (makeModel) lines.push(`Make/model: ${makeModel}`);
      if (a.serialNumber) lines.push(`Serial: ${a.serialNumber}`);
      if (a.assetTag) lines.push(`Asset tag: ${a.assetTag}`);
      lines.push('');
    }
    lines.push(
      `Contact: ${contactName.trim()} · ${contactPhone.trim()}` +
        (contactEmail.trim() ? ` · ${contactEmail.trim()}` : ''),
    );
    lines.push('');
    if (description.trim()) {
      lines.push('Issue description:');
      lines.push(description.trim());
    }

    mutate(
      {
        customerId,
        locationId: defaultLocationId,
        kind,
        subject: subject.trim(),
        body: lines.join('\n'),
      },
      {
        onSuccess: () => {
          setShowOk(true);
          setSubject('');
          setDescription('');
          setAssetId('');
          setTimeout(() => setShowOk(false), 4000);
        },
      },
    );
  }

  const errMsg = error instanceof Error ? error.message : null;
  const canSubmit =
    subject.trim().length > 0 &&
    contactName.trim().length > 0 &&
    contactPhone.trim().length > 0;

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Kind pills */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs tracking-tight font-semibold text-slate-900 mr-1">
          Kind
        </span>
        {KIND_OPTIONS.map((opt) => {
          const selected = kind === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setKind(opt.value)}
              className={cn(
                'text-sm px-3 py-1 rounded-full border transition-colors',
                selected
                  ? 'bg-brix-primary-deep text-white border-brix-primary-deep'
                  : 'bg-brix-surface text-slate-900 border-brix-border hover:bg-brix-surface-alt',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <Field
        label="Equipment having the issue"
        hint={
          assets.length === 0
            ? 'No equipment on file — leave it blank and dispatch will look it up.'
            : 'Pick a unit to attach its serial + make/model to the ticket automatically.'
        }
      >
        <div className="relative">
          <select
            value={assetId}
            onChange={(e) => handleAssetChange(e.target.value)}
            className="w-full h-10 appearance-none rounded-md border border-brix-border bg-brix-surface pl-3 pr-9 text-sm tracking-tight cursor-pointer focus-visible:outline-none focus-visible:border-brix-primary focus-visible:ring-2 focus-visible:ring-brix-primary/20"
          >
            <option value="">— No specific equipment —</option>
            {assets.map((a) => {
              const title =
                a.catalogName ?? a.qboItemName ?? a.description ?? 'Equipment unit';
              const tag = a.serialNumber
                ? ` (SN ${a.serialNumber})`
                : a.assetTag
                  ? ` (${a.assetTag})`
                  : '';
              return (
                <option key={a.id} value={a.id}>
                  {title}
                  {tag}
                </option>
              );
            })}
          </select>
          <ChevronDown
            size={14}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
          />
        </div>
      </Field>

      <Field label="Subject">
        <Input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={140}
          placeholder="Dispenser leaking, ice machine error, etc."
          required
        />
      </Field>

      <Field label="What's going on?">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
          maxLength={2000}
          placeholder="Anything the tech should know before they arrive — symptoms, when it started, what you've tried."
          className="w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm leading-relaxed placeholder:text-brix-muted/70 focus-visible:outline-none focus-visible:border-brix-primary focus-visible:ring-2 focus-visible:ring-brix-primary/20"
        />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Contact name" icon={<User size={13} />}>
          <Input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Who should we ask for on site?"
            required
          />
        </Field>
        <Field label="Callback number" icon={<Phone size={13} />}>
          <Input
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="510-555-0100"
            required
          />
        </Field>
        <Field label="Email" icon={<Mail size={13} />}>
          <Input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="you@yourcompany.com"
          />
        </Field>
      </div>

      {errMsg && (
        <div className="text-xs text-red-600 tracking-tight px-3 py-2 rounded-md border border-red-200 bg-red-50">
          {errMsg}
        </div>
      )}
      {showOk && (
        <div className="text-sm text-emerald-700 tracking-tight px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50 inline-flex items-center gap-2">
          <CheckCircle2 size={14} /> Ticket submitted. Service team's been emailed.
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button type="submit" disabled={isPending || !canSubmit}>
          {isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Submitting…
            </>
          ) : (
            <>
              <Wrench size={14} /> Submit service ticket
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  hint,
  icon,
  children,
}: {
  label: string;
  hint?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs tracking-tight font-semibold text-slate-900 flex items-center gap-1.5">
        {icon}
        {label}
      </Label>
      {children}
      {hint && (
        <p className="text-[11px] text-slate-900/70 tracking-tight italic">{hint}</p>
      )}
    </div>
  );
}

/* ---------------------------- ticket row ---------------------------- */

function TicketRow({ ticket }: { ticket: ServiceRequest }) {
  const update = useUpdateServiceRequestStatus();
  return (
    <li className="py-3 flex items-start gap-3">
      <Wrench size={18} className="text-brix-primary shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-slate-900 truncate min-w-0">
            {ticket.subject}
          </p>
          <select
            value={ticket.status}
            disabled={update.isPending}
            onChange={(e) => {
              const next = e.target.value as ServiceRequestStatus;
              if (next !== ticket.status) {
                update.mutate({ id: ticket.id, status: next });
              }
            }}
            className="shrink-0 text-xs tracking-tight font-semibold border border-brix-border bg-brix-surface text-brix-primary rounded px-1.5 py-0.5 cursor-pointer disabled:opacity-50"
            aria-label="Status"
          >
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <p className="text-xs text-slate-900/80 truncate flex items-center gap-1.5 mt-0.5">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs tracking-tight font-semibold bg-brix-surface-alt text-brix-primary border border-brix-border">
            {kindLabel(ticket.kind)}
          </span>
          <span>·</span>
          <span>{formatRelative(ticket.createdAt)}</span>
        </p>
        {ticket.body && (
          <pre className="text-xs text-slate-900/80 mt-1 whitespace-pre-wrap font-body leading-relaxed">
            {ticket.body}
          </pre>
        )}
      </div>
    </li>
  );
}

/* ---------------------------- SF job row ---------------------------- */

function SfJobRow({
  job,
  tone,
}: {
  job: RecentServiceJob;
  tone: 'open' | 'completed';
}) {
  const pillCls =
    tone === 'open'
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-emerald-50 text-emerald-800 border-emerald-200';
  return (
    <li className="text-sm text-slate-900 tracking-tight flex items-start gap-2 border-b border-brix-border/50 last:border-b-0 pb-2 last:pb-0">
      <Clock size={13} className="text-slate-900 mt-1 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold truncate">
            {job.description ?? `Service job #${job.jobId}`}
          </span>
          <span
            className={cn(
              'shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold border tracking-tight',
              pillCls,
            )}
          >
            {job.status ?? (tone === 'open' ? 'Open' : 'Completed')}
          </span>
        </div>
        <div className="text-xs text-slate-900/70 mt-0.5">
          {job.completedAt ? formatDate(job.completedAt) : '—'}
          {job.technicianName && ` · ${job.technicianName}`}
          {job.location && ` · ${job.location}`}
          <span className="ml-1.5 font-mono text-slate-500">#{job.jobId}</span>
        </div>
      </div>
    </li>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'just now';
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * A chain's corporate login standing on the master gets EVERY store's service history
 * instead of the master account's own handful. Everyone else — a store's own
 * people, and the same person once they pick a store — gets the page above,
 * unchanged.
 *
 * ⚠ A WRAPPER, not a branch inside the page. Returning early above that
 * component's own hooks would change hook order between renders.
 */
export default function ServiceRequests() {
  const { data: group, isLoading } = useGroupAssets();

  // Hold the single-account render until we know which page this is. A flash
  // of corporate's own empty service history before the chain view lands reads as
  // "where did everything go".
  if (isLoading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-brix-muted">
        <ChainSwitchSpinner size={16} className="animate-spin inline-block mr-2" />
        Loading your service history…
      </div>
    );
  }
  if (group?.is_group) return <ChainService />;
  return <SingleServiceRequests />;
}
